import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { ArrowUpRight, Users, CheckCircle2, MailCheck, LineChart, ShieldCheck, Sparkles, Send, Building2, Heart, Clock, QrCode } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHURCH_SIZES = [
  { value: "<50", label: "Under 50" },
  { value: "50-150", label: "50 – 150" },
  { value: "150-500", label: "150 – 500" },
  { value: "500+", label: "500+" },
];

const ROLES = ["Pastor", "Associate Pastor", "Discipleship Coordinator", "Ministry Leader", "Bible Study Teacher", "Other"];

// Add new tools to this list as they ship.
const TOOLS = [
  {
    slug: "tracker",
    brand: "thedisciple",
    brandAccent: "tracker",
    subtitle: "Your Church CRM",
    to: "/crm/first-steps",
    ctaLabel: "Open thedisciple.tracker",
    status: "Live",
    tagline:
      "A quiet, purpose-built place for coordinators and home study teachers to walk every guest from first visit through graduation — without spreadsheets, without losing anyone.",
    features: [
      { icon: Users, label: "Guest tracker across First Steps + Next Steps" },
      { icon: CheckCircle2, label: "Session-by-session progress with notes" },
      { icon: MailCheck, label: "Teacher invitations sent automatically" },
      { icon: LineChart, label: "Weekly team activity roll-up" },
      { icon: ShieldCheck, label: "Multi-church, multi-coordinator scoping" },
    ],
    accent: "var(--ember)",
    accentSoft: "var(--surface-hover)",
  },
  {
    slug: "attendance",
    brand: "thedisciple",
    brandAccent: "attendance",
    subtitle: "Church Body Attendance",
    to: "/attendance/login",
    ctaLabel: "Open thedisciple.attendance",
    status: "Live",
    tagline:
      "Track weekly attendance across your whole congregation. Split the body into sections, assign a leader to each section, and let each leader check off the disciples they care for — service by service.",
    features: [
      { icon: Users, label: "Master disciple list with contact cards" },
      { icon: ShieldCheck, label: "Sections + section leaders with their own login" },
      { icon: CheckCircle2, label: "Weekly, bi-weekly or monthly services" },
      { icon: LineChart, label: "Coordinator overview across every section" },
      { icon: MailCheck, label: "Promote from First Steps → Church Body (coming soon)" },
    ],
    accent: "var(--terracotta)",
    accentSoft: "var(--surface-hover)",
  },
  {
    slug: "prayer",
    brand: "thedisciple",
    brandAccent: "prayer",
    subtitle: "Prayer Log",
    to: "/prayer-admin",
    ctaLabel: "Open thedisciple.prayer",
    status: "Live",
    tagline:
      "A quiet, one-tap prayer log for the people who come to the sanctuary between services. Members find themselves, tap once, and go pray — the leadership sees a faithful picture of who is quietly carrying the church.",
    features: [
      { icon: Heart, label: "One-tap prayer check-in (no timer, no checkout)" },
      { icon: QrCode, label: "Dedicated prayer QR — separate from attendance" },
      { icon: Users, label: "Kiosk mode for a church tablet" },
      { icon: Clock, label: '"Who\u2019s prayed" rolling 24-hour list' },
      { icon: LineChart, label: "Admin dashboard, log, reports & manual entry" },
      { icon: ShieldCheck, label: "Uses your shared directory & permissions" },
    ],
    accent: "var(--gold)",
    accentSoft: "var(--surface-hover)",
  },
];

function ToolCard({ tool, index }) {
  return (
    <article
      className="relative group"
      data-testid={`tool-card-${tool.slug}`}
      style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 lg:p-12">
        {/* Left: brand block */}
        <div className="lg:col-span-5 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <span
                className="text-[0.6rem] uppercase tracking-[0.28em] px-2.5 py-1 rounded-full"
                style={{ background: tool.accent, color: "#FFFFFF" }}
              >
                Tool {String(index + 1).padStart(2, "0")}
              </span>
              <span
                className="text-[0.6rem] uppercase tracking-[0.28em] px-2.5 py-1 rounded-full"
                style={{ background: tool.accentSoft, color: "var(--ink-2)" }}
              >
                {tool.status}
              </span>
            </div>
            <div className="font-serif-display leading-[0.9] tracking-[-0.045em]">
              <span className="block text-4xl sm:text-5xl lg:text-6xl" style={{ color: "var(--ink)" }}>
                {tool.brand}
                <span style={{ color: tool.accent }}>.</span>
              </span>
              <span
                className="block text-4xl sm:text-5xl lg:text-6xl font-serif-display-italic"
                style={{ color: tool.accent }}
              >
                {tool.brandAccent}
              </span>
            </div>
            <div className="mt-6 overline" style={{ fontSize: "0.7rem", color: "var(--ink-2)" }}>
              {tool.subtitle}
            </div>
          </div>

          <div className="mt-10 lg:mt-14">
            {tool.slug === "tracker" ? (
              <>
                <div className="text-[0.6rem] uppercase tracking-[0.24em] mb-3" style={{ color: "var(--muted)" }}>
                  Sign in based on your role
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Link
                    to="/crm/login"
                    data-testid="tool-cta-tracker-coord"
                    className="inline-flex items-center gap-2 text-sm font-medium px-6 py-3 transition-transform hover:-translate-y-0.5"
                    style={{ background: "var(--ink)", color: "#FFFFFF" }}
                  >
                    Coordinator sign-in
                    <ArrowUpRight size={16} />
                  </Link>
                  <Link
                    to="/teacher/login"
                    data-testid="tool-cta-tracker-teacher"
                    className="inline-flex items-center gap-2 text-sm font-medium px-6 py-3 border transition-colors hover:bg-[var(--ink)] hover:text-white"
                    style={{ borderColor: "var(--ink)", color: "var(--ink)" }}
                  >
                    Teacher sign-in
                    <ArrowUpRight size={16} />
                  </Link>
                  <a
                    href="#request-demo"
                    data-testid={`tool-demo-${tool.slug}`}
                    className="inline-flex items-center gap-2 text-sm font-medium px-6 py-3 border transition-colors hover:bg-[var(--surface-hover)]"
                    style={{ borderColor: "var(--border-strong)", color: "var(--ink)" }}
                  >
                    Request a walkthrough
                  </a>
                </div>
              </>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <Link
                  to={tool.to}
                  data-testid={`tool-cta-${tool.slug}`}
                  className="inline-flex items-center gap-2 text-sm font-medium px-6 py-3 transition-transform hover:-translate-y-0.5"
                  style={{ background: "var(--ink)", color: "#FFFFFF" }}
                >
                  {tool.ctaLabel || `Open thedisciple.${tool.brandAccent}`}
                  <ArrowUpRight size={16} />
                </Link>
                <a
                  href="#request-demo"
                  data-testid={`tool-demo-${tool.slug}`}
                  className="inline-flex items-center gap-2 text-sm font-medium px-6 py-3 border transition-colors hover:bg-[var(--surface-hover)]"
                  style={{ borderColor: "var(--border-strong)", color: "var(--ink)" }}
                >
                  Request a walkthrough
                </a>
              </div>
            )}
            <p className="text-[0.7rem] mt-3" style={{ color: "var(--muted)" }}>
              {tool.slug === "attendance"
                ? <>Coordinators + section leaders sign in at <span style={{ color: "var(--ink-2)" }}>thedisciple.life/attendance</span>.</>
                : <>Not sure which one? Coordinators manage the church-wide roster; teachers see the guests assigned to them.</>}
            </p>
          </div>
        </div>

        {/* Right: pitch + feature list */}
        <div className="lg:col-span-7 lg:pl-8 lg:border-l" style={{ borderColor: "var(--border-subtle)" }}>
          <p
            className="text-base lg:text-lg leading-relaxed"
            style={{ color: "var(--ink-2)" }}
            data-testid={`tool-tagline-${tool.slug}`}
          >
            {tool.tagline}
          </p>

          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {tool.features.map((f, i) => {
              const Icon = f.icon;
              return (
                <li key={i} className="flex items-start gap-3">
                  <span
                    className="mt-0.5 shrink-0 flex items-center justify-center rounded-md"
                    style={{ width: 32, height: 32, background: tool.accentSoft, color: tool.accent }}
                  >
                    <Icon size={15} strokeWidth={1.75} />
                  </span>
                  <span className="text-sm" style={{ color: "var(--ink)" }}>
                    {f.label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </article>
  );
}

function ComingSoonCard() {
  return (
    <article
      className="p-10 lg:p-12 text-center"
      style={{
        background: "transparent",
        border: "1px dashed var(--border-strong)",
      }}
      data-testid="tool-card-coming-soon"
    >
      <div className="inline-flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.28em] px-2.5 py-1 rounded-full mb-4"
           style={{ background: "var(--surface-hover)", color: "var(--gold)" }}>
        <Sparkles size={11} /> More on the way
      </div>
      <h3 className="font-serif-display text-2xl lg:text-3xl" style={{ color: "var(--ink)" }}>
        More resources are being built.
      </h3>
      <p className="mt-3 max-w-lg mx-auto text-sm" style={{ color: "var(--muted)" }}>
        Bible study curricula, teacher training kits, sermon libraries — every tool we build for our own church
        will live here for yours too.
      </p>
    </article>
  );
}

export function DemoRequestSection() {
  const [form, setForm] = useState({
    tool: "tracker",
    name: "",
    role: "",
    church_name: "",
    email: "",
    phone: "",
    church_size: "",
    city: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.church_name.trim() || !form.email.trim()) {
      toast.error("Name, church, and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries/church`, form);
      setSubmitted(true);
      toast.success("Thanks — we'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section
        id="request-demo"
        data-testid="demo-request-success"
        className="max-w-4xl mx-auto px-6 lg:px-12 py-24 text-center"
      >
        <div
          className="mx-auto mb-6 flex items-center justify-center rounded-full"
          style={{ width: 72, height: 72, background: "var(--surface-hover)", color: "var(--olive)" }}
        >
          <CheckCircle2 size={30} strokeWidth={1.5} />
        </div>
        <h2 className="font-serif-display text-3xl lg:text-4xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>
          Thanks for reaching out<span style={{ color: "var(--ember)" }}>.</span>
        </h2>
        <p className="mt-4 max-w-xl mx-auto text-base leading-relaxed" style={{ color: "var(--ink-2)" }}>
          Cody or Sam will personally reach out from <strong style={{ color: "var(--ink)" }}>thedisciplelifems@gmail.com</strong> within a couple of days to walk you through the tracker and get your church set up.
        </p>
      </section>
    );
  }

  return (
    <section id="request-demo" data-testid="demo-request-section" className="max-w-6xl mx-auto px-6 lg:px-12 py-24">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left: pitch */}
        <div className="lg:col-span-5">
          <div className="overline mb-4" style={{ color: "var(--ember)" }}>Get set up</div>
          <h2
            className="font-serif-display tracking-[-0.04em] leading-[0.95] text-4xl lg:text-6xl"
            style={{ color: "var(--ink)" }}
          >
            Get your church<br />
            <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>set up.</span>
          </h2>
          <p className="mt-6 text-base lg:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
            Ready to try thedisciple.tracker for your church? Tell us a little about you and we'll personally reach out to walk you through it — pastor to pastor.
          </p>
          <ul className="mt-8 space-y-3 text-sm" style={{ color: "var(--ink-2)" }}>
            {[
              "A 20-minute walkthrough of the tracker",
              "Your own organization set up with your coordinators + teachers",
              "Help importing your first Bible study class",
              "No pressure, no contract — just a real conversation",
            ].map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 size={16} style={{ color: "var(--olive)", marginTop: 2 }} className="shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 p-4 rounded-md text-xs" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }}>
            <Building2 size={13} style={{ color: "var(--gold)" }} className="inline mr-1.5" />
            Prefer email? Write to <a href="mailto:thedisciplelifems@gmail.com" className="underline" style={{ color: "var(--ink)" }}>thedisciplelifems@gmail.com</a>.
          </div>
        </div>

        {/* Right: form */}
        <form
          onSubmit={submit}
          className="lg:col-span-7 card-surface p-8 lg:p-10 space-y-5"
          style={{ background: "var(--bg-alt)" }}
          data-testid="demo-request-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>Your name *</label>
              <input
                required
                value={form.name}
                onChange={set("name")}
                data-testid="demo-name"
                className="w-full px-3 py-2 text-sm"
                placeholder="Pastor John Doe"
              />
            </div>
            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>Role</label>
              <select
                value={form.role}
                onChange={set("role")}
                data-testid="demo-role"
                className="w-full px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[0.65rem] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>Church name *</label>
              <input
                required
                value={form.church_name}
                onChange={set("church_name")}
                data-testid="demo-church-name"
                className="w-full px-3 py-2 text-sm"
                placeholder="Pine Grove Church"
              />
            </div>
            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>Email *</label>
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                data-testid="demo-email"
                className="w-full px-3 py-2 text-sm"
                placeholder="pastor@yourchurch.org"
              />
            </div>
            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                data-testid="demo-phone"
                className="w-full px-3 py-2 text-sm"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>City</label>
              <input
                value={form.city}
                onChange={set("city")}
                data-testid="demo-city"
                className="w-full px-3 py-2 text-sm"
                placeholder="Nashville, TN"
              />
            </div>
            <div>
              <label className="block text-[0.65rem] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>Church size</label>
              <select
                value={form.church_size}
                onChange={set("church_size")}
                data-testid="demo-church-size"
                className="w-full px-3 py-2 text-sm"
              >
                <option value="">Select…</option>
                {CHURCH_SIZES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[0.65rem] uppercase tracking-[0.2em] mb-1" style={{ color: "var(--muted)" }}>Anything you'd like us to know?</label>
            <textarea
              rows={4}
              value={form.message}
              onChange={set("message")}
              data-testid="demo-message"
              className="w-full px-3 py-2 text-sm"
              placeholder="How many Bible studies are you running? Who else on your team should we loop in?"
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-2 flex-wrap">
            <p className="text-[0.7rem]" style={{ color: "var(--muted)" }}>
              We'll only use your details to follow up about thedisciple.tracker.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="demo-submit"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: "var(--ink)", color: "#FFFFFF" }}
            >
              {submitting ? "Sending…" : (<>Request a walkthrough <Send size={14} /></>)}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

export default function Resources() {
  return (
    <div data-testid="resources-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-16">
        <div className="overline mb-4">For local churches</div>
        <h1
          className="font-serif-display tracking-[-0.045em] leading-[0.88]"
          style={{ color: "var(--ink)" }}
          data-testid="resources-title"
        >
          <span className="block text-5xl sm:text-7xl lg:text-8xl">Church</span>
          <span className="block text-5xl sm:text-7xl lg:text-8xl">
            <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>Resources</span>
            <span style={{ color: "var(--ember)" }}>.</span>
          </span>
        </h1>
        <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
          Every tool we build for discipling our own congregation ends up here — so pastors,
          teachers, and coordinators anywhere can put the same practical scaffolding to work.
        </p>
      </section>

      {/* TOOLS LIST */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-12 space-y-8">
        {TOOLS.map((t, i) => (
          <ToolCard key={t.slug} tool={t} index={i} />
        ))}
        <ComingSoonCard />
      </section>

      {/* DEMO REQUEST */}
      <DemoRequestSection />
    </div>
  );
}
